import { useCallback, useEffect, useMemo, useState } from "react";
import { MdKeyboardArrowDown } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import tabEquipmentsIcon from "../../assets/images/tab Equipments.svg";
import tabTyresIcon from "../../assets/images/tab Tyres.svg";
import tabVehiclesIcon from "../../assets/images/tab Vehicles.svg";
import Breadcrumbs from "../../components/Breadcrumbs";
import CustomTabs from "../../components/CustomTabs";
import "../../styles/Access.css";
import "../../styles/AssetsRegistration.css";
import "../../styles/ExportButton.css";
import "../../styles/Services.css";
import "../../styles/TransportersTable.css";
import {
  AppMainModules,
  AssetsPermission,
  AssetsSubModules,
} from "../../utils/app-constant";
import { hasPermission } from "../../utils/helper";
import ExportButton from "../../components/ExportButton";
import { exportDataIcon, plusIcon } from "../../utils/svgs";
import AssetsEquipmentsTab from "./Equipments/AssetsEquipmentsTab";
import { useEquipmentListingExport } from "./Equipments/useEquipmentListingExport";
import AssetsTyresTab from "./Tyres/AssetsTyresTab";
import { useTyresListingExport } from "./Tyres/useTyresListingExport";
import AssetsVehiclesTab from "./Vehicles/AssetsVehiclesTab";
import useVehicleListingExport from "./Vehicles/useVehicleListingExport";

const ASSETS_REGISTRATION_VALID_TABS = ["vehicles", "equipments", "tyres"];

const normalizeAssetsRegistrationTab = (value, fallback = "vehicles") => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();

  return ASSETS_REGISTRATION_VALID_TABS.includes(normalized)
    ? normalized
    : fallback;
};

const getAssetsRegistrationPath = (tab = "vehicles") =>
  `/assets?tab=${normalizeAssetsRegistrationTab(tab)}`;

const appendAssetsRegistrationTab = (path, tab = "vehicles") => {
  const separator = String(path).includes("?") ? "&" : "?";
  return `${path}${separator}tab=${normalizeAssetsRegistrationTab(tab)}`;
};

const getAssetsRegistrationReturnTab = (location, fallback = "vehicles") => {
  const params = new URLSearchParams(location?.search || "");
  return normalizeAssetsRegistrationTab(
    params.get("tab") || location?.state?.activeTab || location?.state?.returnTab,
    fallback
  );
};

const AssetsRegistration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = getAssetsRegistrationReturnTab(location, "vehicles");
  const [showCreateVehicleDropdown, setShowCreateVehicleDropdown] = useState(false);
  const [vehicleExportState, setVehicleExportState] = useState({
    searchText: "",
    searchFilters: {},
    rows: [],
  });
  const {
    hasActiveFilters,
    exportLoading,
    pdfExportLoading,
    excelExportLoading,
    handleExportPdf,
    handleExportExcel,
  } = useVehicleListingExport(vehicleExportState);

  const [equipmentSearchText, setEquipmentSearchText] = useState("");
  const [equipmentSearchFilters, setEquipmentSearchFilters] = useState({});

  const {
    hasActiveFilters: hasActiveEquipmentFilters,
    pdfExportLoading: equipmentPdfExportLoading,
    excelExportLoading: equipmentExcelExportLoading,
    handleExportPdf: handleEquipmentExportPdf,
    handleExportExcel: handleEquipmentExportExcel,
  } = useEquipmentListingExport({
    searchText: equipmentSearchText,
    searchFilters: equipmentSearchFilters,
  });

  const [tyreSearchText, setTyreSearchText] = useState("");
  const [tyreSearchFilters, setTyreSearchFilters] = useState({});

  const {
    hasActiveFilters: hasActiveTyreFilters,
    pdfExportLoading: tyrePdfExportLoading,
    excelExportLoading: tyreExcelExportLoading,
    handleExportPdf: handleTyreExportPdf,
    handleExportExcel: handleTyreExportExcel,
  } = useTyresListingExport({
    searchText: tyreSearchText,
    searchFilters: tyreSearchFilters,
  });
  const canViewVehicles = hasPermission({
    permissionSlug: AssetsPermission.ViewVehicle,
    menuSlug: AssetsSubModules.Assets,
    moduleSlug: AppMainModules.assetsManagement,
  });
  const canViewEquipments = hasPermission({
    permissionSlug: AssetsPermission.ViewEquipment,
    menuSlug: AssetsSubModules.Assets,
    moduleSlug: AppMainModules.assetsManagement,
  });
  const canViewTyres = hasPermission({
    permissionSlug: AssetsPermission.ViewTyre,
    menuSlug: AssetsSubModules.Assets,
    moduleSlug: AppMainModules.assetsManagement,
  });
  const canCreateVehicles = hasPermission({
    permissionSlug: AssetsPermission.CreateVehicle,
    menuSlug: AssetsSubModules.Assets,
    moduleSlug: AppMainModules.assetsManagement,
  });
  const canCreateEquipments = hasPermission({
    permissionSlug: AssetsPermission.CreateEquipment,
    menuSlug: AssetsSubModules.Assets,
    moduleSlug: AppMainModules.assetsManagement,
  });
  const canCreateTyres = hasPermission({
    permissionSlug: AssetsPermission.CreateTyre,
    menuSlug: AssetsSubModules.Assets,
    moduleSlug: AppMainModules.assetsManagement,
  });

  const allowedTabs = useMemo(
    () =>
      [
        {
          key: "vehicles",
          label: "Vehicles",
          icon: (
            <img
              src={tabVehiclesIcon}
              alt="Vehicles"
              className="assets_registration_tab_icon"
            />
          ),
          isAllowed: canViewVehicles,
        },
        {
          key: "equipments",
          label: "Equipments",
          icon: (
            <img
              src={tabEquipmentsIcon}
              alt="Equipments"
              className="assets_registration_tab_icon"
            />
          ),
          isAllowed: canViewEquipments,
        },
        {
          key: "tyres",
          label: "Tyres",
          icon: (
            <img src={tabTyresIcon} alt="Tyres" className="assets_registration_tab_icon" />
          ),
          isAllowed: canViewTyres,
        },
      ].filter((tab) => tab.isAllowed),
    [canViewEquipments, canViewTyres, canViewVehicles]
  );
  const allowedTabKeys = useMemo(
    () => allowedTabs.map((tab) => tab.key),
    [allowedTabs]
  );
  const resolvedActiveTab = allowedTabKeys.includes(activeTab)
    ? activeTab
    : allowedTabKeys[0] || activeTab;

  useEffect(() => {
    if (resolvedActiveTab !== "vehicles") {
      setShowCreateVehicleDropdown(false);
    }
  }, [resolvedActiveTab]);

  useEffect(() => {
    if (!allowedTabKeys.length || allowedTabKeys.includes(activeTab)) return;

    const fallbackTab = allowedTabKeys[0];
    setShowCreateVehicleDropdown(false);
    navigate(getAssetsRegistrationPath(fallbackTab), {
      replace: true,
      state: { ...(location?.state || {}), activeTab: fallbackTab },
    });
  }, [activeTab, allowedTabKeys, location?.state, navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest(".create_vehicle_options") &&
        !event.target.closest(".create_vehicle_btn")
      ) {
        setShowCreateVehicleDropdown(false);
      }
    };

    if (showCreateVehicleDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCreateVehicleDropdown]);

  const handleTabChange = useCallback(
    (nextTab) => {
      const normalizedTab = normalizeAssetsRegistrationTab(nextTab, "vehicles");
      if (normalizedTab === resolvedActiveTab) return;

      setShowCreateVehicleDropdown(false);
      navigate(getAssetsRegistrationPath(normalizedTab), {
        replace: true,
        state: { ...(location?.state || {}), activeTab: normalizedTab },
      });
    },
    [location?.state, navigate, resolvedActiveTab]
  );

  const breadcrumbValues = useMemo(
    () => [
      "Assets",
      resolvedActiveTab === "equipments"
        ? "Equipments"
        : resolvedActiveTab === "tyres"
          ? "Tyres"
          : "Vehicles",
    ],
    [resolvedActiveTab]
  );

  const renderActiveTabContent = () => {
    if (resolvedActiveTab === "equipments") {
      return (
        <AssetsEquipmentsTab
          activeTab={resolvedActiveTab}
          searchText={equipmentSearchText}
          setSearchText={setEquipmentSearchText}
          searchFilters={equipmentSearchFilters}
          setSearchFilters={setEquipmentSearchFilters}
        />
      );
    }

    if (resolvedActiveTab === "tyres") {
      return (
        <AssetsTyresTab
          activeTab={resolvedActiveTab}
          searchText={tyreSearchText}
          setSearchText={setTyreSearchText}
          searchFilters={tyreSearchFilters}
          setSearchFilters={setTyreSearchFilters}
        />
      );
    }

    return (
      <AssetsVehiclesTab
        activeTab={resolvedActiveTab}
        onExportStateChange={setVehicleExportState}
      />
    );
  };

  return (
    <div className="assets_registration_page">
      <Breadcrumbs values={breadcrumbValues} />

      <div className="access_view">
        <h2>Assets</h2>

        <div className="services_buttons assets_registration_actions">
          {resolvedActiveTab === "vehicles" && (
            <>
              <button
                type="button"
                className="export_btn"
                onClick={handleExportPdf}
                disabled={!hasActiveFilters || exportLoading}
                aria-busy={pdfExportLoading}
                title={!hasActiveFilters ? "Select a search term or filter first" : undefined}
              >
                {exportDataIcon} {pdfExportLoading ? "Opening..." : "PDF Download"}
              </button>

              <button
                type="button"
                className="export_btn"
                onClick={handleExportExcel}
                disabled={!hasActiveFilters || exportLoading}
                aria-busy={excelExportLoading}
                title={!hasActiveFilters ? "Select a search term or filter first" : undefined}
              >
                {exportDataIcon} {excelExportLoading ? "Downloading..." : "Excel Download"}
              </button>
            </>
          )}

          {resolvedActiveTab === "vehicles" && canCreateVehicles && (
            <div style={{ position: "relative" }}>
              <button
                className="create_vehicle_btn"
                onClick={() => setShowCreateVehicleDropdown((previous) => !previous)}
                style={{ display: "flex", alignItems: "center", gap: "6px", color: "#fff" }}
              >
                Create Vehicle
                <MdKeyboardArrowDown style={{ fontSize: "20px" }} />
              </button>
              {showCreateVehicleDropdown && (
                <div
                  className="create_vehicle_options"
                  style={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    marginTop: "8px",
                    background: "#fff",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    padding: "8px 0",
                    zIndex: 10,
                    minWidth: "160px",
                  }}
                >
                  <div
                    onClick={() => {
                      setShowCreateVehicleDropdown(false);
                      navigate(
                        appendAssetsRegistrationTab(
                          "/assets/create-purchased-vehicle",
                          resolvedActiveTab
                        ),
                        { state: { activeTab: resolvedActiveTab } }
                      );
                    }}
                    style={{
                      padding: "10px 20px",
                      display: "flex",
                      alignItems: "center",
                      color: "#4d5171",
                      cursor: "pointer",
                      fontSize: "15px",
                      fontWeight: "500",
                    }}
                    onMouseEnter={(event) => {
                      event.target.style.background = "#f4f5f8";
                    }}
                    onMouseLeave={(event) => {
                      event.target.style.background = "transparent";
                    }}
                  >
                    Purchased
                  </div>
                  {/* Hired / Contract flow hidden for now. */}
                  {/* <div
                    onClick={() => {
                      setShowCreateVehicleDropdown(false);
                      navigate(
                        appendAssetsRegistrationTab(
                          "/assets/create-hired-vehicle",
                          resolvedActiveTab
                        ),
                        { state: { activeTab: resolvedActiveTab } }
                      );
                    }}
                    style={{
                      padding: "10px 20px",
                      display: "flex",
                      alignItems: "center",
                      color: "#4d5171",
                      cursor: "pointer",
                      fontSize: "15px",
                      fontWeight: "500",
                    }}
                    onMouseEnter={(event) => {
                      event.target.style.background = "#f4f5f8";
                    }}
                    onMouseLeave={(event) => {
                      event.target.style.background = "transparent";
                    }}
                  >
                    Hired / Contract
                  </div> */}
                  <div
                    onClick={() => {
                      setShowCreateVehicleDropdown(false);
                      navigate(
                        appendAssetsRegistrationTab(
                          "/assets/create-3pl-vehicle",
                          resolvedActiveTab
                        ),
                        { state: { activeTab: resolvedActiveTab } }
                      );
                    }}
                    style={{
                      padding: "10px 20px",
                      display: "flex",
                      alignItems: "center",
                      color: "#4d5171",
                      cursor: "pointer",
                      fontSize: "15px",
                      fontWeight: "500",
                    }}
                    onMouseEnter={(event) => {
                      event.target.style.background = "#f4f5f8";
                    }}
                    onMouseLeave={(event) => {
                      event.target.style.background = "transparent";
                    }}
                  >
                    Hired / Contract
                  </div>
                </div>
              )}
            </div>
          )}

          {resolvedActiveTab === "equipments" && (
            <>
              <ExportButton
                type="pdf"
                onClick={handleEquipmentExportPdf}
                disabled={!hasActiveEquipmentFilters || equipmentPdfExportLoading}
                loading={equipmentPdfExportLoading}
                hasActiveFilters={hasActiveEquipmentFilters}
              />

              <ExportButton
                type="excel"
                onClick={handleEquipmentExportExcel}
                disabled={!hasActiveEquipmentFilters || equipmentExcelExportLoading}
                loading={equipmentExcelExportLoading}
                hasActiveFilters={hasActiveEquipmentFilters}
              />
            </>
          )}

          {resolvedActiveTab === "equipments" && canCreateEquipments && (
            <button
              type="button"
              onClick={() =>
                navigate(
                  appendAssetsRegistrationTab("/assets/create-equipment", resolvedActiveTab),
                  { state: { activeTab: resolvedActiveTab } }
                )
              }
            >
              {plusIcon} Add Equipment
            </button>
          )}

          {resolvedActiveTab === "tyres" && (
            <>
              <ExportButton
                type="pdf"
                onClick={handleTyreExportPdf}
                disabled={!hasActiveTyreFilters || tyrePdfExportLoading}
                loading={tyrePdfExportLoading}
                hasActiveFilters={hasActiveTyreFilters}
              />

              <ExportButton
                type="excel"
                onClick={handleTyreExportExcel}
                disabled={!hasActiveTyreFilters || tyreExcelExportLoading}
                loading={tyreExcelExportLoading}
                hasActiveFilters={hasActiveTyreFilters}
              />
            </>
          )}

          {resolvedActiveTab === "tyres" && canCreateTyres && (
            <button
              type="button"
              onClick={() =>
                navigate(
                  appendAssetsRegistrationTab("/assets/create-tyre", resolvedActiveTab),
                  { state: { activeTab: resolvedActiveTab } }
                )
              }
            >
              {plusIcon} Add Tyre
            </button>
          )}
        </div>
      </div>

      <div className="assets_registration_tabs_wrap">
        <CustomTabs
          tabs={allowedTabs}
          activeTab={resolvedActiveTab}
          setActiveTab={handleTabChange}
        />
      </div>

      {renderActiveTabContent()}
    </div>
  );
};

export default AssetsRegistration;
